import { Button } from '@econsult/econsult-ui/dist/components/Button';
import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { Tooltip } from '@mui/material';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ErrorToast } from 'components/Toast';
import { STRINGS } from 'config';
import { format, parseISO } from 'date-fns';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import React, { useCallback, useEffect, useState } from 'react';
import { getOrdersPage } from 'store/action/wallet';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { Order } from 'store/reducer/wallet';
import styled from 'styled-components';
import { ParagraphXsmallRegular } from 'styles/elements/typography';
import theme from 'styles/theme';
import { Label } from './balance';

const {
  REDUCERS: {
    WALLET: { GET_ORDERS_REQUEST, GET_ORDERS_SUCCESS, GET_ORDERS_ERROR },
  },
} = STRINGS;

export const StatusChip = ({ status }: { status: string }) => {
  const statusColors: Record<string, { bg: string; color: string }> = {
    PAID: {
      bg: theme.colorStatusSuccessBg,
      color: theme.colorStatusSuccess,
    },
    PENDING: {
      bg: theme.colorStatusWarningBg,
      color: theme.colorStatusWarning,
    },
    EXPIRED: {
      bg: theme.colorStatusErrorBg,
      color: theme.colorStatusError,
    },
    REFUNDED: {
      bg: theme.colorStatusImportantBg,
      color: theme.colorStatusImportant,
    },
  };

  return (
    <Chip
      label={status}
      sx={{
        backgroundColor: statusColors[status]?.bg,
        color: statusColors[status]?.color,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    />
  );
};

const TableContainer = styled.div`
  margin-top: 20px;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledTable = styled(Table)`
  && {
    border-collapse: collapse;
    flex: 1;
  }
`;

const HeaderCell = styled(TableCell)`
  && {
    padding: 0 0;
  }
`;

const DataCell = styled(TableCell)`
  && {
    color: ${({ theme }) => theme.colorPrimaryPrimary70};
    font-size: 13px;
    padding: 12px 0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 0;
  color: ${({ theme }) => theme.colorNeutralNeutral1};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  background: ${({ theme }) => theme.colorNeutralNeutral10};
  position: sticky;
  bottom: 0;
  z-index: 10;
`;

const PageInfo = styled.span`
  color: ${({ theme }) => theme.colorNeutralNeutral1};
  font-size: 14px;
  font-weight: 600;
`;

const formatDateTime = (dateTime: string) => format(parseISO(dateTime), 'MMM dd, yyyy • HH:mm');

export const OrderTable = () => {
  const {
    wallet,
    app: { selectedPractice },
  } = useAppState();
  const dispatch = useAppDispatch();
  const { orders } = wallet as { orders: Order[] | null };
  const selectedPracticeId = selectedPractice?.id || '';
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNextFlag, setHasNextFlag] = useState(true);

  const pageSize = 10;
  const totalPages = orders ? Math.ceil(orders.length / pageSize) : 0;
  const paginatedOrders = orders ? orders.slice(currentPage * pageSize, (currentPage + 1) * pageSize) : [];
  const filteredOrders = paginatedOrders?.filter((order: Order) => order.status !== 'EXPIRED');
  const handlePrevious = () => setCurrentPage(prev => Math.max(prev - 1, 0));
  const handleNext = () => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));

  const fetchPages = useCallback(
    async (page = 1, results: Order[] = []) => {
      const { body: pageResults, hasNext } = await getOrdersPage(page, selectedPracticeId);
      const newResults = [...results, ...pageResults];
      dispatch({
        type: GET_ORDERS_SUCCESS,
        payload: newResults,
      });
      if (hasNext) {
        return fetchPages(page + 1, newResults); // recursive call
      }
      setHasNextFlag(false);
      return newResults;
    },
    [dispatch, selectedPracticeId],
  );

  useEffect(() => {
    let isMounted = true;
    if (hasNextFlag) {
      dispatch({ type: GET_ORDERS_REQUEST });
      fetchPages().catch(error => {
        if (isMounted) {
          dispatch({ type: GET_ORDERS_ERROR, payload: error });
          toast.error(React.createElement(ErrorToast, { message: 'Could not retrieve orders' }), {
            autoClose: 12000,
            pauseOnHover: true,
            hideProgressBar: false,
            closeOnClick: true,
            icon: false,
          });
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [orders, dispatch, fetchPages, hasNextFlag]);

  if (orders === null || _isEmpty(orders) || _isEmpty(filteredOrders)) {
    return (
      <TableContainer>
        <EmptyState>
          <ParagraphXsmallRegular data-automation="NoOrders">
            No orders found. Please contact support if you believe this is an error.
          </ParagraphXsmallRegular>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <StyledTable aria-label="orders-table">
        <TableHead>
          <TableRow data-automation="TableHeaderRow">
            <HeaderCell>
              <Label>Order ID</Label>
            </HeaderCell>
            <HeaderCell>
              <Label>Status</Label>
            </HeaderCell>
            <HeaderCell>
              <Label>Type</Label>
            </HeaderCell>
            <HeaderCell>
              <Label>Label</Label>
            </HeaderCell>
            <HeaderCell>
              <Label>Created</Label>
            </HeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {_map(filteredOrders, (order: Order) => {
            const { _id: id } = order; // alias ts fix
            const firstItem = order.details?.items?.[0];
            return (
              <TableRow data-automation="TableRow" key={id}>
                <DataCell>
                  <span style={{ fontFamily: 'monospace', fontSize: '12px', padding: 0 }}>{id}</span>
                </DataCell>
                <DataCell>
                  <StatusChip status={order.status} />
                </DataCell>
                <DataCell>
                  <ParagraphXsmallRegular style={{ color: theme.colorPrimaryPrimary70 }}>
                    {firstItem?.type || '-'}
                  </ParagraphXsmallRegular>
                </DataCell>
                <DataCell>
                  <ParagraphXsmallRegular style={{ color: theme.colorPrimaryPrimary70 }}>
                    {firstItem?.label?.toUpperCase() || '-'}
                  </ParagraphXsmallRegular>
                </DataCell>
                <DataCell>
                  <ParagraphXsmallRegular style={{ color: theme.colorPrimaryPrimary70 }}>
                    {formatDateTime(order.createdAt).toUpperCase()}
                  </ParagraphXsmallRegular>
                </DataCell>
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>

      {totalPages > 1 && (
        <PaginationContainer>
          <Tooltip title="Newer" placement="bottom" enterDelay={800}>
            <Button
              size="small"
              state="primary"
              id="AskCopilotButton"
              data-automation="AskCopilotButton"
              onClick={handlePrevious}
              disabled={currentPage === 0}
              aria-label="Previous page"
            >
              {'<'}
            </Button>
          </Tooltip>
          <PageInfo>
            {currentPage + 1} / {totalPages}
          </PageInfo>
          <Tooltip title="Older" placement="bottom" enterDelay={800}>
            <Button
              size="small"
              state="primary"
              id="AskCopilotButton"
              data-automation="AskCopilotButton"
              onClick={handleNext}
              disabled={currentPage === totalPages - 1}
              aria-label="Next page"
            >
              {'>'}
            </Button>
          </Tooltip>
        </PaginationContainer>
      )}
    </TableContainer>
  );
};
